import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import MuiLink from '@material-ui/core/Link';
import { useMutation } from 'react-apollo';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import InfoIcon from '@material-ui/icons/Info';
import { ReactComponent as EditIcon } from 'assets/icons/editIcon.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import withConfirm from 'hooks/withConfirm';
import Essays from '../cell';
import StatusSelect from '../status';
import {
  UPDATE_DOCUMENT,
  DELETE_DOCUMENT,
  GET_UNIVERSITIES,
} from 'graphql/student/essays';
import DueDateModal from './DueDateModal';
import PRODUCTS from 'constant/product-types';

const useStyles = makeStyles((theme) => ({
  actions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }, actionBtn: {
    padding: theme.spacing(1),
  },
  essayText: {
    flexGrow: 1,
    display: 'block',
    lineHeight: 'auto',
    marginTop: 10,
    marginLeft: 10
  },
  header: {
    marginTop: 10,
    marginLeft: 10

  }

}));

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const UniCard = (props) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const profile = useSelector((s) => s.student.profile);
  const [updateDoc] = useMutation(UPDATE_DOCUMENT);
  const [deleteDoc] = useMutation(DELETE_DOCUMENT);
  const popupState = usePopupState({ variant: 'popover', popupId: 'menu' });
  const university = props.data;
  const [showDateModal, setDateModal] = useState(false);
  const [selected, setSelected] = useState();


  const onChangeStatus = ({ id, status, doc }) => {
    if (status === 'draft' && user.type !== 'student') {
      window.analytics.track('Essay Status changed', {
        ...doc,
        status,
        id,
        product: PRODUCTS_LOOKUP[props.student.product_type],
      });
      setSelected({
        status,
        id,
      });
      return setDateModal(true);
    }

    window.analytics.track('Essay Status changed', {
      ...doc,
      status,
      id,
      product: PRODUCTS_LOOKUP[props.student.product_type],
    });

    return updateDoc({
      variables: {
        id,
        data: {
          status,
        },
      },
    });
  };

  const handleEdit = () => {
    props.onEdit(university);
    popupState.close();
  };

  const handleDelete = () => {
    popupState.close();
    props.confirm(() => {
      props.onDelete(university);
    });
  };

  const handleLogs = () => {
    popupState.close();
    props.onViewLogs(university);
  };

  const handleAddDoc = () => {
    popupState.close();
    props.onAddDocument(university);
  };

  const handleUpdateDoc = (doc) => {
    popupState.close();
    props.onUpdateDoc(university, doc);
  };

  const onDeleteDoc = (doc) => {
    props.confirm(() => {
      deleteDoc({
        variables: { id: doc.id },
        update: (proxy) => {
          const queryVariables = { usersub: university.usersub };
          const { universities } = proxy.readQuery({
            query: GET_UNIVERSITIES,
            variables: queryVariables,
          });
          const uni = universities.find((u) => u.id === doc.university_id);
          uni.documents = uni.documents.filter((d) => d !== doc);
          proxy.writeQuery({
            query: GET_UNIVERSITIES,
            variables: queryVariables,
            data: { universities },
          });
        },
      });
    });
  };

  return (
    <Card>
      <CardHeader
        className={classes.header}
        action={
          <IconButton {...bindTrigger(popupState)}>
            <MoreVertIcon color="disabled" />
          </IconButton>
        }
        title={university?.schools?.map(i => i.label).join(', ')}
        titleTypographyProps={{
          variant: 'subtitle1',
          gutterBottom: true,
        }}
        subheader={`Due ${moment.utc(university.due_date).format('LL')}`}
        subheaderTypographyProps={{
          variant: 'caption',
          style: { opacity: 0.6 },
        }}
        style={{ paddingBottom: 0 }}
      />
      <CardContent style={{ paddingBottom: 0, marginBottom: 26 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box marginRight={1} marginLeft={1} lineHeight={0}>
            <InfoIcon fontSize={'small'} color="disabled" />{' '}
          </Box>
          <Typography variant="caption">
            Check university page to select essay and add topic below
          </Typography>
        </Box>
        <Box lineHeight={1.3} marginBottom={5} marginLeft={1}>
          {university?.schools?.map((i) => (
            <MuiLink
              style={{ marginRight: 8 }}
              component={Link}
              to={
                user.type === 'student'
                  ? `/schools/list/${i.value}`
                  : `/students/${props.student.user_id}/schools/list/${i.value}`
              }
            >
              {i.label}
            </MuiLink>
          ))}
        </Box>
        {university?.documents.map((doc, index) => (
          <Box key={doc.id} marginBottom={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
            >
              <Box maxWidth={300} display="flex" flexDirection="column">
                <Essays readOnly={props.readOnly} doc={doc} />
                <Typography variant="caption" style={{ opacity: 0.6, marginLeft: 10, marginTop: 3 }}>
                  {doc.status === 'draft' && doc.draft_due_date
                    ? moment
                      .utc(doc.draft_due_date)
                      .format('[Draft due] MMM DD, YYYY')
                    : null}
                </Typography>
                <Box >
                  <Typography className={classes.essayText} variant="caption">{doc.prompt}</Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection={{ xs: 'row', sm: 'column' }}
                alignItems={{ xs: 'center', sm: 'flex-end' }}
                justifyContent={{ xs: 'space-between', sm: 'center' }}
                marginTop={{ xs: 2, sm: 0 }}
              >
                <StatusSelect
                  doc={doc}
                  readOnly={props.readOnly}
                  id={doc.id}
                  value={doc.status}
                  onChangeStatus={onChangeStatus}
                  student={props.student}
                  hasHCCredit={props.hasHCCredit}
                  hasCCCredit={props.hasCCCredit}
                  hasExpertCredit={props.hasExpertCredit}
                  profile={profile}
                  user={user}
                />

                <Box display="flex" alignItems="center" marginTop={{ sm: 1 }}>

                  {!props.readOnly && (
                    <Box marginLeft={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateDoc(doc)}
                        className={classes.actionBtn}
                        style={{ minWidth: "45px" }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => onDeleteDoc(doc)}
                        className={classes.actionBtn}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Divider className={classes.divider} />
          </Box>
        ))}
      </CardContent>
      <CardActions className={classes.actions}>
        {!props.readOnly && (
          <Button
            variant="outlined"
            size="large"
            color="primary"
            onClick={handleAddDoc}
          >
            Add Essay
          </Button>
        )}
      </CardActions>
      <Menu {...bindMenu(popupState)}>
        {!props.readOnly && (
          <>
            <MenuItem onClick={handleEdit}>Edit</MenuItem>
            <Divider />
          </>
        )}
        <MenuItem onClick={handleLogs}>Logs</MenuItem>
        {user.type !== 'student' && !props.readOnly && (<><Divider /><MenuItem onClick={handleDelete}>Delete</MenuItem></>)}
      </Menu>
      {showDateModal && (
        <DueDateModal
          student={props.student}
          doc={selected}
          onClose={() => {
            setDateModal(false);
          }}
        />
      )}
    </Card>
  );
};

export default withConfirm(UniCard);
